import React, { useState, useEffect, useCallback } from 'react';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  DatePicker,
  message,
  Row,
  Col,
  Statistic,
  Typography,
  Popconfirm,
  Tooltip,
  Badge,
  Drawer,
  Timeline,
  Divider,
  Spin,
  Empty,
  Alert
} from 'antd';
import {
  PlusOutlined,
  CheckOutlined,
  CloseOutlined,
  CalendarOutlined,
  UserOutlined,
  ClockCircleOutlined,
  FileTextOutlined,
  ReloadOutlined,
  EyeOutlined,
  DeleteOutlined,
  EditOutlined
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import {
  leaveService,
  employeeService,
  Employee,
  LeaveRequest,
  LeaveBalance,
  LeaveType
} from '../../../../services/hrService';

const { Title, Text } = Typography;
const { TextArea } = Input;
const { RangePicker } = DatePicker;
const { Option } = Select;

// Leave types
const LEAVE_TYPES = [
  { value: 'VACATION', label: 'Vacation', color: 'blue' },
  { value: 'SICK', label: 'Sick Leave', color: 'orange' },
  { value: 'PERSONAL', label: 'Personal', color: 'purple' },
  { value: 'MATERNITY', label: 'Maternity', color: 'pink' },
  { value: 'PATERNITY', label: 'Paternity', color: 'cyan' },
  { value: 'BEREAVEMENT', label: 'Bereavement', color: 'default' },
];

// Leave status
const LEAVE_STATUSES = [
  { value: 'PENDING', label: 'Pending', color: 'gold' },
  { value: 'APPROVED', label: 'Approved', color: 'green' },
  { value: 'REJECTED', label: 'Rejected', color: 'red' },
];

// Extended interface for display purposes
interface LeaveRequestDisplay extends LeaveRequest {
  employee?: {
    id: string;
    firstName: string;
    lastName: string;
    department?: { name: string };
  };
}

const LeaveManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [leaveRequests, setLeaveRequests] = useState<LeaveRequest[]>([]);
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<LeaveRequest | null>(null);
  const [isRejectModalVisible, setIsRejectModalVisible] = useState(false);
  const [rejectingRequestId, setRejectingRequestId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [form] = Form.useForm();

  // Filter states
  const [filters, setFilters] = useState({
    status: '',
    leaveType: '',
    employeeId: ''
  });

  // Stats
  const [stats, setStats] = useState({
    total: 0,
    pending: 0,
    approved: 0,
    rejected: 0
  });

  // Fetch leave requests
  const fetchLeaveRequests = useCallback(async () => {
    setLoading(true);
    try {
      const data = await leaveService.getAll();
      setLeaveRequests(data || []);

      // Calculate stats
      const requests = data || [];
      setStats({
        total: requests.length,
        pending: requests.filter((r: LeaveRequest) => r.status === 'PENDING').length,
        approved: requests.filter((r: LeaveRequest) => r.status === 'APPROVED').length,
        rejected: requests.filter((r: LeaveRequest) => r.status === 'REJECTED').length
      });
    } catch (error: any) {
      console.error('Error fetching leave requests:', error);
      message.error('Failed to load leave requests');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch employees for dropdown
  const fetchEmployees = useCallback(async () => {
    try {
      const data = await employeeService.getAll();
      setEmployees(data || []);
    } catch (error) {
      console.error('Error fetching employees:', error);
    }
  }, []);

  useEffect(() => {
    fetchLeaveRequests();
    fetchEmployees();
  }, [fetchLeaveRequests, fetchEmployees]);

  // Get leave type info
  const getLeaveTypeInfo = (type: string) => {
    return LEAVE_TYPES.find(t => t.value === type) || { label: type, color: 'default' };
  };

  // Get status info
  const getStatusInfo = (status: string) => {
    return LEAVE_STATUSES.find(s => s.value === status) || { label: status, color: 'default' };
  };

  // Calculate days between dates
  const calculateDays = (startDate: string, endDate: string) => {
    const start = dayjs(startDate);
    const end = dayjs(endDate);
    return end.diff(start, 'day') + 1;
  };

  // Handle create leave request
  const handleCreateRequest = async (values: any) => {
    try {
      setLoading(true);
      const data = {
        employeeId: values.employeeId,
        leaveType: values.leaveType as LeaveType,
        startDate: values.dateRange[0].format('YYYY-MM-DD'),
        endDate: values.dateRange[1].format('YYYY-MM-DD'),
        reason: values.reason
      };

      await leaveService.create(data);
      message.success('Leave request created successfully');
      setIsModalVisible(false);
      form.resetFields();
      fetchLeaveRequests();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to create leave request');
    } finally {
      setLoading(false);
    }
  };

  // Handle approve
  const handleApprove = async (requestId: string) => {
    try {
      await leaveService.approve(requestId);
      message.success('Leave request approved');
      fetchLeaveRequests();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to approve request');
    }
  };

  // Handle reject with reason
  const handleReject = async (requestId: string, reason?: string) => {
    try {
      await leaveService.reject(requestId, reason || 'Request rejected');
      message.success('Leave request rejected');
      fetchLeaveRequests();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to reject request');
    }
  };

  // Handle delete
  const handleDelete = async (requestId: string) => {
    try {
      await leaveService.delete(requestId);
      message.success('Leave request deleted');
      fetchLeaveRequests();
    } catch (error: any) {
      message.error(error.response?.data?.message || 'Failed to delete request');
    }
  };

  // Open reject modal
  const openRejectModal = (requestId: string) => {
    setRejectingRequestId(requestId);
    setRejectionReason('');
    setIsRejectModalVisible(true);
  };

  // Submit rejection
  const submitRejection = async () => {
    if (!rejectingRequestId) return;
    await handleReject(rejectingRequestId, rejectionReason);
    setIsRejectModalVisible(false);
    setRejectingRequestId(null);
    setRejectionReason('');
    setSelectedRequest(null);
  };

  // Filter leave requests
  const filteredRequests = leaveRequests.filter(req => {
    const matchesStatus = !filters.status || req.status === filters.status;
    const matchesType = !filters.leaveType || req.leaveType === filters.leaveType;
    const matchesEmployee = !filters.employeeId || req.employeeId === filters.employeeId;
    return matchesStatus && matchesType && matchesEmployee;
  });

  // Table columns
  const columns: ColumnsType<LeaveRequest> = [
    {
      title: 'Employee',
      key: 'employee',
      width: 200,
      render: (_, record) => (
        <Space>
          <UserOutlined />
          <div>
            <div style={{ fontWeight: 500 }}>
              {record.employee ? `${record.employee.firstName} ${record.employee.lastName}` : 'Unknown'}
            </div>
            <Text type="secondary" style={{ fontSize: 12 }}>
              {record.employee?.department?.name || '-'}
            </Text>
          </div>
        </Space>
      )
    },
    {
      title: 'Leave Type',
      dataIndex: 'leaveType',
      key: 'leaveType',
      width: 120,
      render: (type) => {
        const info = getLeaveTypeInfo(type);
        return <Tag color={info.color}>{info.label}</Tag>;
      }
    },
    {
      title: 'Duration',
      key: 'duration',
      width: 200,
      render: (_, record) => (
        <div>
          <div>
            <CalendarOutlined style={{ marginRight: 8 }} />
            {dayjs(record.startDate).format('MMM DD')} - {dayjs(record.endDate).format('MMM DD, YYYY')}
          </div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {calculateDays(record.startDate, record.endDate)} day(s)
          </Text>
        </div>
      )
    },
    {
      title: 'Reason',
      dataIndex: 'reason',
      key: 'reason',
      ellipsis: true,
      width: 200
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status) => {
        const info = getStatusInfo(status);
        return <Tag color={info.color}>{info.label}</Tag>;
      }
    },
    {
      title: 'Requested',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (date) => dayjs(date).format('MMM DD, YYYY')
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space>
          <Tooltip title="View Details">
            <Button
              type="text"
              icon={<EyeOutlined />}
              onClick={() => setSelectedRequest(record)}
            />
          </Tooltip>
          {record.status === 'PENDING' && (
            <>
              <Popconfirm
                title="Approve this leave request?"
                onConfirm={() => handleApprove(record.id)}
                okText="Approve"
                cancelText="Cancel"
              >
                <Tooltip title="Approve">
                  <Button type="text" icon={<CheckOutlined />} style={{ color: '#52c41a' }} />
                </Tooltip>
              </Popconfirm>
              <Tooltip title="Reject">
                <Button
                  type="text"
                  icon={<CloseOutlined />}
                  danger
                  onClick={() => openRejectModal(record.id)}
                />
              </Tooltip>
              <Popconfirm
                title="Delete this leave request?"
                onConfirm={() => handleDelete(record.id)}
                okText="Delete"
                cancelText="Cancel"
              >
                <Tooltip title="Delete">
                  <Button type="text" icon={<DeleteOutlined />} danger />
                </Tooltip>
              </Popconfirm>
            </>
          )}
        </Space>
      )
    }
  ];

  return (
    <Spin spinning={loading}>
      <div>
        {/* Stats Cards */}
        <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Total Requests"
                value={stats.total}
                prefix={<FileTextOutlined />}
                valueStyle={{ color: '#1890ff' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Pending"
                value={stats.pending}
                prefix={<ClockCircleOutlined />}
                valueStyle={{ color: '#faad14' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Approved"
                value={stats.approved}
                prefix={<CheckOutlined />}
                valueStyle={{ color: '#52c41a' }}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Rejected"
                value={stats.rejected}
                prefix={<CloseOutlined />}
                valueStyle={{ color: '#f5222d' }}
              />
            </Card>
          </Col>
        </Row>

        {/* Filters and Actions */}
        <Card>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col xs={24} sm={12} md={6}>
              <Select
                placeholder="Filter by Employee"
                style={{ width: '100%' }}
                value={filters.employeeId || undefined}
                onChange={(value) => setFilters({ ...filters, employeeId: value })}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Leave Type"
                style={{ width: '100%' }}
                value={filters.leaveType || undefined}
                onChange={(value) => setFilters({ ...filters, leaveType: value })}
                allowClear
              >
                {LEAVE_TYPES.map(type => (
                  <Option key={type.value} value={type.value}>{type.label}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={4}>
              <Select
                placeholder="Status"
                style={{ width: '100%' }}
                value={filters.status || undefined}
                onChange={(value) => setFilters({ ...filters, status: value })}
                allowClear
              >
                {LEAVE_STATUSES.map(status => (
                  <Option key={status.value} value={status.value}>{status.label}</Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={10} style={{ textAlign: 'right' }}>
              <Space>
                <Button icon={<ReloadOutlined />} onClick={fetchLeaveRequests}>
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={() => {
                    form.resetFields();
                    setIsModalVisible(true);
                  }}
                >
                  New Leave Request
                </Button>
              </Space>
            </Col>
          </Row>

          {filteredRequests.length === 0 && !loading ? (
            <Empty description="No leave requests found" />
          ) : (
            <Table
              columns={columns}
              dataSource={filteredRequests}
              rowKey="id"
              pagination={{
                defaultPageSize: 10,
                showSizeChanger: true,
                showTotal: (total) => `Total ${total} requests`
              }}
              scroll={{ x: 1100 }}
            />
          )}
        </Card>

        {/* Create Leave Request Modal */}
        <Modal
          title="New Leave Request"
          open={isModalVisible}
          onCancel={() => {
            setIsModalVisible(false);
            form.resetFields();
          }}
          footer={null}
          width={500}
          destroyOnClose
        >
          <Form form={form} layout="vertical" onFinish={handleCreateRequest}>
            <Form.Item
              name="employeeId"
              label="Employee"
              rules={[{ required: true, message: 'Please select an employee' }]}
            >
              <Select
                placeholder="Select employee"
                showSearch
                optionFilterProp="children"
              >
                {employees.map(emp => (
                  <Option key={emp.id} value={emp.id}>
                    {emp.firstName} {emp.lastName}
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="leaveType"
              label="Leave Type"
              rules={[{ required: true, message: 'Please select leave type' }]}
            >
              <Select placeholder="Select leave type">
                {LEAVE_TYPES.map(type => (
                  <Option key={type.value} value={type.value}>
                    <Tag color={type.color}>{type.label}</Tag>
                  </Option>
                ))}
              </Select>
            </Form.Item>

            <Form.Item
              name="dateRange"
              label="Leave Period"
              rules={[{ required: true, message: 'Please select leave dates' }]}
            >
              <RangePicker
                style={{ width: '100%' }}
                disabledDate={(current) => current && current < dayjs().startOf('day')}
              />
            </Form.Item>

            <Form.Item
              name="reason"
              label="Reason"
              rules={[{ required: true, message: 'Please provide a reason' }]}
            >
              <TextArea rows={3} placeholder="Enter reason for leave..." />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
              <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
                <Button onClick={() => {
                  setIsModalVisible(false);
                  form.resetFields();
                }}>
                  Cancel
                </Button>
                <Button type="primary" htmlType="submit" loading={loading}>
                  Submit Request
                </Button>
              </Space>
            </Form.Item>
          </Form>
        </Modal>

        {/* Leave Request Details Drawer */}
        <Drawer
          title="Leave Request Details"
          placement="right"
          width={400}
          open={!!selectedRequest}
          onClose={() => setSelectedRequest(null)}
        >
          {selectedRequest && (
            <div>
              <div style={{ textAlign: 'center', marginBottom: 24 }}>
                <Badge
                  status={
                    selectedRequest.status === 'APPROVED' ? 'success' :
                    selectedRequest.status === 'REJECTED' ? 'error' : 'processing'
                  }
                  text={
                    <Text strong style={{ fontSize: 16 }}>
                      {getStatusInfo(selectedRequest.status).label}
                    </Text>
                  }
                />
              </div>

              <Divider />

              <Row gutter={[16, 16]}>
                <Col span={12}>
                  <Text type="secondary">Employee</Text>
                  <div style={{ fontWeight: 500 }}>
                    {selectedRequest.employee
                      ? `${selectedRequest.employee.firstName} ${selectedRequest.employee.lastName}`
                      : 'Unknown'}
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Department</Text>
                  <div>{selectedRequest.employee?.department?.name || '-'}</div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Leave Type</Text>
                  <div>
                    <Tag color={getLeaveTypeInfo(selectedRequest.leaveType).color}>
                      {getLeaveTypeInfo(selectedRequest.leaveType).label}
                    </Tag>
                  </div>
                </Col>
                <Col span={12}>
                  <Text type="secondary">Duration</Text>
                  <div>{calculateDays(selectedRequest.startDate, selectedRequest.endDate)} day(s)</div>
                </Col>
                <Col span={24}>
                  <Text type="secondary">Leave Period</Text>
                  <div>
                    {dayjs(selectedRequest.startDate).format('MMM DD, YYYY')} - {dayjs(selectedRequest.endDate).format('MMM DD, YYYY')}
                  </div>
                </Col>
                <Col span={24}>
                  <Text type="secondary">Reason</Text>
                  <div>{selectedRequest.reason}</div>
                </Col>
                <Col span={24}>
                  <Text type="secondary">Requested On</Text>
                  <div>{dayjs(selectedRequest.createdAt).format('MMM DD, YYYY HH:mm')}</div>
                </Col>
                {selectedRequest.approvedAt && (
                  <Col span={24}>
                    <Text type="secondary">
                      {selectedRequest.status === 'APPROVED' ? 'Approved' : 'Rejected'} On
                    </Text>
                    <div>{dayjs(selectedRequest.approvedAt).format('MMM DD, YYYY HH:mm')}</div>
                  </Col>
                )}
              </Row>

              {selectedRequest.status === 'PENDING' && (
                <>
                  <Divider />
                  <Space style={{ width: '100%', justifyContent: 'center' }}>
                    <Popconfirm
                      title="Approve this leave request?"
                      onConfirm={() => {
                        handleApprove(selectedRequest.id);
                        setSelectedRequest(null);
                      }}
                    >
                      <Button type="primary" icon={<CheckOutlined />}>
                        Approve
                      </Button>
                    </Popconfirm>
                    <Button
                      danger
                      icon={<CloseOutlined />}
                      onClick={() => openRejectModal(selectedRequest.id)}
                    >
                      Reject
                    </Button>
                  </Space>
                </>
              )}

              {selectedRequest.rejectionReason && (
                <>
                  <Divider />
                  <Alert
                    type="error"
                    message="Rejection Reason"
                    description={selectedRequest.rejectionReason}
                    showIcon
                  />
                </>
              )}
            </div>
          )}
        </Drawer>

        {/* Rejection Reason Modal */}
        <Modal
          title="Reject Leave Request"
          open={isRejectModalVisible}
          onCancel={() => {
            setIsRejectModalVisible(false);
            setRejectingRequestId(null);
            setRejectionReason('');
          }}
          onOk={submitRejection}
          okText="Reject"
          okButtonProps={{ danger: true }}
        >
          <div style={{ marginBottom: 16 }}>
            <Text>Please provide a reason for rejecting this leave request:</Text>
          </div>
          <TextArea
            rows={4}
            placeholder="Enter rejection reason..."
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
          />
        </Modal>
      </div>
    </Spin>
  );
};

export default LeaveManagement;
